import { useState } from "react";
import { CheckCircle, PauseCircle, ChefHat, Trash2, ChevronsUpDown, Loader2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import type { BulkSessionAction } from "@/types/cooking-session";

export type { BulkSessionAction };

interface BulkActionsBarProps {
  count: number;
  availableActions: BulkSessionAction[];
  onAction: (action: BulkSessionAction) => Promise<void>;
  className?: string;
}

export function BulkActionsBar({ count, availableActions, onAction, className }: BulkActionsBarProps) {
  const { t } = useLingui();
  const [isPending, setIsPending] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (count === 0 || availableActions.length === 0) return null;

  const has = (a: BulkSessionAction) => availableActions.includes(a);

  async function handleAction(action: BulkSessionAction) {
    setIsPending(true);
    try { await onAction(action); } finally { setIsPending(false); }
  }

  return (
    <>
      <div
        className={cn(
          "fixed bottom-5 left-1/2 -translate-x-1/2 z-50",
          "flex items-center justify-between w-76 bg-(--color-action) rounded-lg px-4 py-4 shadow-[0px_24px_48px_-12px_rgba(16,24,40,0.18)]",
          className,
        )}
      >
        <div className="flex items-center gap-2 text-base leading-none">
          <span className="text-(--color-action-fg)/80">{t`Selected:`}</span>
          <span className="font-medium text-(--color-action-fg)">{count}</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              disabled={isPending}
              className="h-11 px-3 gap-2 bg-(--color-action-fg)/10 hover:bg-(--color-action-fg)/20 text-(--color-action-fg) hover:text-(--color-action-fg) rounded-md disabled:opacity-60"
            >
              {isPending ? (
                <Loader2 className="h-5 w-5 animate-spin shrink-0" />
              ) : (
                <>
                  <span className="text-base font-medium">{t`Actions`}</span>
                  <ChevronsUpDown className="h-5 w-5 shrink-0" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 p-1.5">
            {has("COMPLETED") && (
              <DropdownMenuItem onClick={() => handleAction("COMPLETED")} className="gap-2 px-2 py-3">
                <CheckCircle className="h-5 w-5 shrink-0" />
                {t`Mark as Completed`}
              </DropdownMenuItem>
            )}
            {has("PAUSED") && (
              <DropdownMenuItem onClick={() => handleAction("PAUSED")} className="gap-2 px-2 py-3">
                <PauseCircle className="h-5 w-5 shrink-0" />
                {t`Mark as Paused`}
              </DropdownMenuItem>
            )}
            {has("COOKING") && (
              <DropdownMenuItem onClick={() => handleAction("COOKING")} className="gap-2 px-2 py-3">
                <ChefHat className="h-5 w-5 shrink-0" />
                {t`Mark as Cooking`}
              </DropdownMenuItem>
            )}
            {has("DELETE") && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowDeleteConfirm(true)}
                  className="gap-2 px-2 py-3 text-(--color-danger-text) focus:text-(--color-danger-text)"
                >
                  <Trash2 className="h-5 w-5 shrink-0" />
                  {t`Delete selected`}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={t`Delete selected`}
        description={t`This will permanently delete ${count} session${count !== 1 ? "s" : ""}. This action cannot be undone.`}
        onConfirm={() => onAction("DELETE")}
      />
    </>
  );
}
